import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Store } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useMe } from "@/context/MeContext";
import { publicGet } from "@/lib/api";
import { PriceSteps } from "@/components/club/PriceSteps";
import { AvailabilityBadge } from "@/components/club/StatusBadge";
import { Seo } from "@/components/Seo";
import { PointsEarn, PointsNeeded } from "@/components/club/PointsEarn";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1768047845974-a1830dfb186a?crop=entropy&cs=srgb&fm=jpg&q=80&w=800";

export function OfferCard({ offer, status }) {
  return (
    <Link to={`/marketplace/${offer.id}`} data-testid={`market-offer-${offer.id}`} className="group fade-up overflow-hidden rounded-3xl border border-white/8 bg-[#12141C] transition-transform duration-300 hover:-translate-y-1">
      <div className={`relative aspect-[4/3] overflow-hidden ${offer.subcategory ? "bg-white" : ""}`}>
        <img src={offer.image_url || FALLBACK_IMG} alt={offer.name} loading="lazy" onError={(e) => (e.currentTarget.src = FALLBACK_IMG)} className={`h-full w-full transition-transform duration-500 group-hover:scale-105 ${offer.subcategory ? "object-contain p-4" : "object-cover"}`} />
        <span className="absolute left-3 top-3 max-w-[60%] truncate rounded-full bg-black/60 px-3 py-1 text-xs text-white backdrop-blur">{offer.subcategory || offer.category}</span>
        <span className="absolute right-3 top-3 inline-flex items-center gap-1 rounded-full bg-amber-500/90 px-2 py-1 text-[10px] font-bold uppercase text-black"><Store className="h-3 w-3" /> {offer.partner_id === "house-ingrosso" ? "Ingrosso club" : "Partner del club"}</span>
      </div>
      <div className="space-y-3 p-5">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">{offer.brand || offer.partner_name}</p>
          <p className="line-clamp-2 font-display text-lg font-semibold text-white">{offer.name}</p>
        </div>
        <PriceSteps offer={offer} current={status} compact />
        <PointsEarn amount={offer[`price_${status}`]} multiplier={offer.partner_id === "house-ingrosso" ? 3 : 1} label={offer.partner_id === "house-ingrosso" ? "Punti x3" : undefined} testid={`market-offer-points-${offer.id}`} />
        <PointsNeeded amount={offer[`price_${status}`]} note="spedizione esclusa" testid={`market-offer-points-needed-${offer.id}`} />
        <AvailabilityBadge offer={offer} testid={`market-offer-availability-${offer.id}`} />
      </div>
    </Link>
  );
}

const PAGE = 60;

export default function Marketplace() {
  const { me, config } = useMe();
  const [offers, setOffers] = useState(null);
  const [more, setMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [partners, setPartners] = useState([]);
  const [subs, setSubs] = useState([]);
  const [q, setQ] = useState("");
  const [category, setCategory] = useState("all");
  const [sub, setSub] = useState("all");
  const [partner, setPartner] = useState("all");

  useEffect(() => {
    publicGet("/marketplace/partners").then(setPartners).catch(() => {});
  }, []);

  useEffect(() => {
    setSub("all");
    setSubs([]);
    if (category !== "all") publicGet("/marketplace/subcategories", { category }).then(setSubs).catch(() => {});
  }, [category]);

  const params = (skip) => ({ q, category: category === "all" ? "" : category, subcategory: sub === "all" ? "" : sub,
    partner_id: partner === "all" ? "" : partner, skip, limit: PAGE });

  useEffect(() => {
    const t = setTimeout(() => {
      publicGet("/marketplace/offers", params(0))
        .then((r) => { setOffers(r); setMore(r.length === PAGE); })
        .catch(() => setOffers([]));
    }, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, category, sub, partner]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const r = await publicGet("/marketplace/offers", params(offers.length));
      setOffers([...offers, ...r]);
      setMore(r.length === PAGE);
    } finally {
      setLoadingMore(false);
    }
  };

  const status = me?.status || "bronze";
  return (
    <div className="mx-auto max-w-7xl px-4 py-14 sm:px-6" data-testid="marketplace-page">
      <Seo title={sub !== "all" ? `${sub}: prezzi all'ingrosso` : category !== "all" ? category : "Marketplace: elettronica all'ingrosso e offerte partner"}
        description="Oltre 6.000 prodotti di elettronica, informatica e consumabili a prezzi d'ingrosso, più offerte delle aziende partner. Prezzi Bronze, Gold e Diamond e punti su ogni acquisto." />
      <p className="font-club text-xs text-amber-400">MARKETPLACE PARTNER</p>
      <h1 className="mt-3 font-display text-4xl font-extrabold tracking-tight text-white sm:text-5xl">Offerte riservate ai membri</h1>
      <p className="mt-4 max-w-2xl text-slate-400">Ogni offerta ha tre prezzi: il tuo è quello del tuo status. Sali di livello per pagare meno.</p>
      <div className={`mt-8 grid gap-3 ${subs.length ? "sm:grid-cols-2 lg:grid-cols-[1fr_220px_240px_220px]" : "sm:grid-cols-[1fr_220px_220px]"}`}>
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-slate-500" />
          <Input data-testid="market-search-input" placeholder="Cerca per nome o marca…" value={q} onChange={(e) => setQ(e.target.value)} className="h-10 bg-[#12141C] pl-9" />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="market-category-filter" className="bg-[#12141C]"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le categorie</SelectItem>
            {(config?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {subs.length > 0 && (
          <Select value={sub} onValueChange={setSub}>
            <SelectTrigger data-testid="market-subcategory-filter" className="bg-[#12141C]"><SelectValue placeholder="Sottocategoria" /></SelectTrigger>
            <SelectContent className="max-h-80">
              <SelectItem value="all">Tutte le sottocategorie</SelectItem>
              {subs.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        )}
        <Select value={partner} onValueChange={setPartner}>
          <SelectTrigger data-testid="market-partner-filter" className="bg-[#12141C]"><SelectValue placeholder="Azienda" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le aziende</SelectItem>
            {partners.map((p) => <SelectItem key={p.id} value={p.id}>{p.company_name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {offers && offers.length === 0 && <p data-testid="market-empty" className="mt-16 text-center text-slate-500">Nessuna offerta trovata.</p>}
      <div className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {(offers || []).map((o) => <OfferCard key={o.id} offer={o} status={status} />)}
      </div>
      {more && (
        <div className="mt-10 text-center">
          <button data-testid="market-load-more" disabled={loadingMore} onClick={loadMore} className="rounded-full border border-white/15 px-6 py-3 text-sm font-semibold text-white hover:bg-white/10 disabled:opacity-60">
            {loadingMore ? "Caricamento…" : "Mostra altri prodotti"}
          </button>
        </div>
      )}
    </div>
  );
}
